import { useState, useEffect, useRef, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useQueryClient } from '@tanstack/react-query';
import { fetchAuthSession } from 'aws-amplify/auth';
import {
  useStudyGroup,
  useFriends,
  useMe,
  authedFetch,
  type FriendRow,
  type StudyGroupMemberDetail,
} from '../lib/api';
import { qrDataUrl } from '../lib/qr';

const WS_URL = import.meta.env.VITE_WS_API_URL ?? '';

// ── WebSocket hook ───────────────────────────────────────────────────────────

function useLobbySocket(
  roomCode: string | undefined,
  onMessage: (msg: any) => void,
) {
  const wsRef = useRef<WebSocket | null>(null);
  const reconnectAttempt = useRef(0);
  const reconnectTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const onMessageRef = useRef(onMessage);
  onMessageRef.current = onMessage;
  // Actions sent while the socket is not OPEN are buffered and flushed on
  // (re)connect, so a Ready/Start tap during a brief reconnect is never lost.
  const queueRef = useRef<Record<string, unknown>[]>([]);
  const closedRef = useRef(false); // set on unmount so the socket does NOT reconnect after navigating away
  const [connected, setConnected] = useState(false);

  const flush = () => {
    if (wsRef.current?.readyState !== WebSocket.OPEN) return;
    const pending = queueRef.current;
    queueRef.current = [];
    for (const p of pending) {
      try { wsRef.current.send(JSON.stringify(p)); } catch { /* re-queued below */ queueRef.current.push(p); }
    }
  };

  const connect = useCallback(async () => {
    if (!roomCode || !WS_URL || closedRef.current) return;
    try {
      const session = await fetchAuthSession();
      const token = session.tokens?.accessToken?.toString();
      if (!token) return;

      const ws = new WebSocket(`${WS_URL}?room=${roomCode}&token=${token}`);
      wsRef.current = ws;

      ws.onopen = () => { reconnectAttempt.current = 0; setConnected(true); flush(); };
      ws.onmessage = (e) => {
        try {
          const msg = JSON.parse(e.data);
          if (msg.type === 'ping') {
            ws.send(JSON.stringify({ action: 'pong' }));
            return;
          }
          onMessageRef.current(msg);
        } catch { /* ignore */ }
      };
      ws.onclose = () => {
        setConnected(false);
        if (closedRef.current) return; // navigated away / unmounted: do NOT reconnect (would steal the play socket's registration)
        const delay = Math.min(30_000, 1000 * Math.pow(2, reconnectAttempt.current));
        reconnectAttempt.current += 1;
        reconnectTimer.current = setTimeout(connect, delay);
      };
      ws.onerror = () => { ws.close(); };
    } catch { /* auth error */ }
  }, [roomCode]);

  const send = useCallback((data: Record<string, unknown>) => {
    if (wsRef.current?.readyState === WebSocket.OPEN) {
      wsRef.current.send(JSON.stringify(data));
      return true;
    }
    // pong is a reply to a ping and only meaningful while connected; never queue it.
    if (data.action !== 'pong') queueRef.current.push(data);
    return false;
  }, []);

  useEffect(() => {
    closedRef.current = false;
    connect();
    return () => {
      closedRef.current = true; // stop the post-close reconnect from re-registering this socket after we leave the lobby
      if (reconnectTimer.current) clearTimeout(reconnectTimer.current);
      wsRef.current?.close();
    };
  }, [connect]);

  return { send, connected };
}

// ── Page ─────────────────────────────────────────────────────────────────────

export function StudyGroupLobbyPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const qc = useQueryClient();
  const { data, isLoading } = useStudyGroup(id!);
  const friends = useFriends();
  const me = useMe();
  const myUserId = me.data?.user.id;

  const [copied, setCopied] = useState(false);
  const [showInviteModal, setShowInviteModal] = useState(false);
  const [invitedIds, setInvitedIds] = useState<Set<string>>(new Set());
  const [inviteErrors, setInviteErrors] = useState<Record<string, string>>({});
  // Readiness, driven by `ready_state` broadcasts.
  const [readyUserIds, setReadyUserIds] = useState<string[]>([]);
  // Optimistic ready state so the button reacts instantly; reconciled by the server echo.
  const [optimisticReady, setOptimisticReady] = useState<boolean | null>(null);

  const group = data?.group;
  const members: StudyGroupMemberDetail[] = data?.members ?? [];
  const activeMembers = members.filter((m) => m.status !== 'left');

  // Determine if current user is creator
  const isCreator = members.some((m) => m.isCreator && m.userId === group?.creator_user_id);

  // WebSocket
  const { send, connected } = useLobbySocket(group?.room_code, (msg) => {
    switch (msg.type) {
      case 'player_joined':
      case 'player_left':
      case 'player_disconnected':
      case 'player_reconnected':
        qc.invalidateQueries({ queryKey: ['study-group', id] });
        break;
      case 'ready_state':
        setReadyUserIds(Array.isArray(msg.readyUserIds) ? msg.readyUserIds : []);
        break;
      case 'countdown':
      case 'question':
        navigate(`/study-groups/${id}/play`);
        break;
      case 'lobby_expired':
        navigate('/study-groups');
        break;
    }
  });

  const serverReady = !!myUserId && readyUserIds.includes(myUserId);
  const iAmReady = optimisticReady ?? serverReady;
  // Clear the optimistic flag once the server echo catches up.
  useEffect(() => {
    if (optimisticReady !== null && optimisticReady === serverReady) setOptimisticReady(null);
  }, [serverReady, optimisticReady]);
  // Reflect the optimistic toggle in the displayed count.
  let readyCount = readyUserIds.length;
  if (optimisticReady === true && myUserId && !readyUserIds.includes(myUserId)) readyCount += 1;
  if (optimisticReady === false && myUserId && readyUserIds.includes(myUserId)) readyCount -= 1;

  // Redirect if game already started
  useEffect(() => {
    if (group?.status === 'active' || group?.status === 'countdown') {
      navigate(`/study-groups/${id}/play`);
    } else if (group?.status === 'completed') {
      navigate(`/study-groups/${id}/results`);
    } else if (group?.status === 'expired') {
      navigate('/study-groups');
    }
  }, [group?.status, id, navigate]);

  const handleCopyCode = async () => {
    if (!group) return;
    try {
      await navigator.clipboard.writeText(group.room_code);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch { /* fallback handled below */ }
  };

  const handleShare = async () => {
    if (!group) return;
    const shareUrl = `https://app.squishyschool.com/groups/join/${group.room_code}`;
    const shareText = `Join my study group on Squishy School. Code: ${group.room_code}\n${shareUrl}`;
    if (navigator.share) {
      try { await navigator.share({ title: 'Join my Study Group', text: shareText }); } catch { /* cancelled */ }
    } else {
      await navigator.clipboard.writeText(shareText);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    }
  };

  const handleInviteFriend = async (friendId: string) => {
    if (!group) return;
    setInviteErrors((prev) => { const n = { ...prev }; delete n[friendId]; return n; });
    try {
      await authedFetch(`/study-groups/${group.id}/invite`, {
        method: 'POST',
        body: JSON.stringify({ friendId }),
      });
      setInvitedIds((prev) => new Set(prev).add(friendId));
    } catch (err: any) {
      const msg = /not_pro|Pro/.test(String(err?.message))
        ? 'Needs Pro to join'
        : 'Could not invite';
      setInviteErrors((prev) => ({ ...prev, [friendId]: msg }));
    }
  };

  // Force-start (host only) — overrides the ready gate.
  const handleStart = () => {
    send({ action: 'start' });
  };

  const handleToggleReady = () => {
    const next = !iAmReady;
    setOptimisticReady(next);      // instant feedback; reconciled by ready_state echo
    send({ action: 'ready', ready: next });
  };

  const handleRemovePlayer = async (userId: string) => {
    try {
      await authedFetch(`/study-groups/${id}/players/${userId}`, { method: 'DELETE' });
      qc.invalidateQueries({ queryKey: ['study-group', id] });
    } catch { /* ignore */ }
    send({ action: 'remove_player', userId });
  };

  const [disbanding, setDisbanding] = useState(false);
  const handleDisband = async () => {
    if (!confirm('Disband this study group? This cannot be undone.')) return;
    setDisbanding(true);
    try {
      await authedFetch(`/study-groups/${id}`, { method: 'DELETE' });
      qc.invalidateQueries({ queryKey: ['study-groups'] });
      navigate('/study-groups', { replace: true });
    } catch {
      setDisbanding(false);
    }
  };

  if (isLoading || !group) {
    return (
      <div className="flex items-center justify-center min-h-[60vh] text-slate-500">
        Loading lobby...
      </div>
    );
  }

  const playerCount = activeMembers.length;
  const canStart = playerCount >= 2;
  const friendsList = (friends.data?.friends ?? []).filter((f) => f.status === 'accepted');

  return (
    <div className="max-w-lg mx-auto pb-24 px-4 space-y-4">
      {/* ── Header ────────────────────────────────────────────── */}
      <div className="flex items-center gap-3">
        <button
          onClick={() => navigate('/study-groups')}
          className="text-slate-500 hover:text-squishy-dark transition-colors"
          aria-label="Back"
        >
          <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
            <path strokeLinecap="round" strokeLinejoin="round" d="M15 19l-7-7 7-7" />
          </svg>
        </button>
        <h1 className="text-xl font-bold text-squishy-dark">Lobby</h1>
      </div>

      {/* ── Room info card ────────────────────────────────────── */}
      <div className="bg-white border border-slate-200 rounded-xl p-4 text-center space-y-2">
        <div className="text-[11px] text-slate-500 uppercase tracking-wide font-semibold">Room Code</div>
        <button
          onClick={handleCopyCode}
          className="text-3xl font-mono font-bold text-squishy-dark tracking-[0.3em] hover:text-squishy-primary transition-colors"
          title="Copy code"
        >
          {group.room_code}
        </button>
        {copied && <div className="text-xs text-emerald-600">Copied!</div>}
        <div className="text-[11px] text-slate-500">
          {group.title} · {group.question_count} questions · {group.timer_seconds}s timer
        </div>
        <RoomQr roomCode={group.room_code} />
        {/* Invite Friends is the primary action; Share Link secondary. Own lines, full width. */}
        <div className="space-y-2 pt-1">
          <button
            onClick={() => setShowInviteModal(true)}
            className="w-full bg-squishy-primary hover:bg-teal-600 text-white text-base font-semibold py-3 rounded-xl transition-colors"
          >
            Invite Friends
          </button>
          <button
            onClick={handleShare}
            className="w-full bg-slate-100 hover:bg-slate-200 text-slate-700 text-sm font-medium py-2.5 rounded-xl transition-colors"
          >
            Share Link
          </button>
        </div>
      </div>

      {/* ── Player list ───────────────────────────────────────── */}
      <div className="bg-white border border-slate-200 rounded-xl p-4">
        <h2 className="text-sm font-semibold text-squishy-dark mb-3">
          Players ({playerCount}/{group.member_cap})
        </h2>
        <ul className="space-y-2">
          {activeMembers.map((p) => (
            <li key={p.userId} className="flex items-center gap-3">
              <div className="relative">
                <div className="w-9 h-9 rounded-full bg-slate-200 flex items-center justify-center text-sm font-medium text-slate-700 overflow-hidden">
                  {p.avatarUrl ? (
                    <img src={p.avatarUrl} alt="" className="w-full h-full object-cover" />
                  ) : (
                    p.displayName[0]?.toUpperCase()
                  )}
                </div>
              </div>
              <div className="flex-1">
                <div className="text-sm font-medium text-slate-800">{p.displayName}</div>
                {p.isCreator && (
                  <div className="text-xs text-squishy-accent font-medium">Host</div>
                )}
              </div>
              {readyUserIds.includes(p.userId) ? (
                <span className="text-xs font-semibold text-emerald-600 bg-emerald-50 dark:bg-emerald-500/20 px-2 py-0.5 rounded-full">
                  Ready
                </span>
              ) : (
                <span className="text-xs text-slate-400">Not ready</span>
              )}
              {isCreator && !p.isCreator && (
                <button
                  onClick={() => handleRemovePlayer(p.userId)}
                  className="text-xs text-slate-400 hover:text-red-500 transition-colors"
                >
                  Remove
                </button>
              )}
            </li>
          ))}
        </ul>
      </div>

      {/* ── Connection status ──────────────────────────────────── */}
      {!connected && (
        <div className="flex items-center justify-center gap-2 text-xs text-amber-600 bg-amber-50 dark:bg-amber-500/10 rounded-lg py-2">
          <span className="w-2 h-2 rounded-full bg-amber-400 animate-pulse" />
          Connecting to live session...
        </div>
      )}

      {/* ── Ready toggle (everyone) ────────────────────────────── */}
      <button
        onClick={handleToggleReady}
        disabled={!connected}
        className={`w-full font-semibold py-3 rounded-xl transition-all disabled:opacity-50 ${
          iAmReady
            ? 'bg-emerald-500 hover:bg-emerald-600 text-white'
            : 'bg-squishy-primary hover:bg-teal-600 text-white'
        }`}
      >
        {iAmReady ? `Ready ✓  ·  ${readyCount}/${playerCount} ready` : `I'm Ready  ·  ${readyCount}/${playerCount} ready`}
      </button>
      <p className="text-center text-xs text-slate-500 -mt-2">
        The game starts automatically once everyone is ready.
      </p>

      {/* ── Host force-start ───────────────────────────────────── */}
      {isCreator && (
        <button
          onClick={handleStart}
          disabled={!canStart || !connected}
          className="w-full bg-white border border-squishy-primary/40 text-squishy-primary font-semibold py-3 rounded-xl disabled:opacity-50 transition-all"
        >
          {canStart
            ? `Start now (${playerCount} players)`
            : 'Need at least 2 players'}
        </button>
      )}

      {/* ── Disband button (creator only) ──────────────────────── */}
      {isCreator && (
        <button
          onClick={handleDisband}
          disabled={disbanding}
          className="w-full text-red-500 hover:text-red-600 text-sm font-medium py-2 transition-colors disabled:opacity-50"
        >
          {disbanding ? 'Disbanding...' : 'Disband Group'}
        </button>
      )}

      {/* ── Invite friends modal ──────────────────────────────── */}
      {showInviteModal && (
        <div className="fixed inset-0 z-50 flex items-end sm:items-center justify-center bg-black/40">
          <div className="bg-white rounded-t-2xl sm:rounded-2xl w-full max-w-md max-h-[70vh] flex flex-col overflow-hidden">
            <div className="flex items-center justify-between p-4 border-b">
              <h3 className="font-semibold text-squishy-dark">Invite Friends</h3>
              <button
                onClick={() => setShowInviteModal(false)}
                className="text-slate-400 hover:text-slate-600"
              >
                <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                  <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>
            <div className="overflow-y-auto p-4 space-y-2 flex-1">
              {friendsList.length === 0 ? (
                <p className="text-sm text-slate-500 text-center py-6">
                  No friends yet. Add friends to invite them.
                </p>
              ) : (
                friendsList.map((f) => {
                  const alreadyInvited = invitedIds.has(f.id);
                  return (
                    <div key={f.id} className="flex items-center gap-3 py-2">
                      <FriendAvatar friend={f} />
                      <div className="flex-1 text-sm font-medium text-slate-800">
                        {formatFriendName(f)}
                      </div>
                      <div className="flex flex-col items-end">
                        <button
                          onClick={() => handleInviteFriend(f.id)}
                          disabled={alreadyInvited}
                          className={`text-xs font-medium px-3 py-1 rounded-full transition-colors ${
                            alreadyInvited
                              ? 'bg-emerald-100 dark:bg-emerald-500/20 text-emerald-600'
                              : 'bg-squishy-primary text-white hover:bg-teal-600'
                          }`}
                        >
                          {alreadyInvited ? 'Sent' : 'Invite'}
                        </button>
                        {inviteErrors[f.id] && (
                          <span className="text-[10px] text-amber-600 mt-0.5">{inviteErrors[f.id]}</span>
                        )}
                      </div>
                    </div>
                  );
                })
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// ── Helpers ──────────────────────────────────────────────────────────────────

function formatFriendName(f: FriendRow): string {
  const real = `${f.first_name} ${f.last_name?.[0] ?? ''}.`;
  // Accepted friends always see your real name, even if you're anonymous / use a handle.
  if (f.status === 'accepted') return real;
  if (f.display_name_mode === 'custom' && f.custom_handle) return `@${f.custom_handle}`;
  if (f.display_name_mode === 'anonymous') return `Student #${f.id.slice(0, 4).toUpperCase()}`;
  return real;
}

function FriendAvatar({ friend }: { friend: FriendRow }) {
  const name = formatFriendName(friend);
  return (
    <div className="w-8 h-8 rounded-full bg-slate-200 flex items-center justify-center text-xs font-medium text-slate-700 overflow-hidden">
      {name[0]?.replace('@', '')}
    </div>
  );
}

function RoomQr({ roomCode }: { roomCode: string }) {
  const [src, setSrc] = useState<string | null>(null);
  useEffect(() => {
    qrDataUrl(`https://app.squishyschool.com/groups/join/${roomCode}`).then(setSrc).catch(() => {});
  }, [roomCode]);
  if (!src) return null;
  return (
    <div className="flex flex-col items-center gap-0.5">
      <img src={src} alt="Scan to join this study group" className="w-24 h-24 rounded-lg border border-slate-100" />
      <div className="text-[10px] text-slate-400">Scan to join</div>
    </div>
  );
}
